"use client";
import React, { useState, useMemo, useRef, useEffect } from "react";
import { Check, ChevronDown, PlusCircle, Search } from "lucide-react";
import { Page } from "@/types/models/page";
import { cn } from "@/lib/utils";

interface PageSelectDropdownProps {
  pages: Page[];
  selectedPageId?: string;
  onPageChange?: (pageId: string) => void;
  isLoading?: boolean;
  isCompact?: boolean;
  /** When set, shows a top action to add/import pages (dropdown opens even if list is empty). */
  onOpenAddPage?: () => void;
  /** Stretch to parent width (e.g. Web Builder sidebar) instead of fixed toolbar width. */
  fluid?: boolean;
}

export function PageSelectDropdown({
  pages,
  selectedPageId,
  onPageChange,
  isLoading = false,
  isCompact = false,
  onOpenAddPage,
  fluid = false,
}: PageSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 10 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const selectedPage = pages.find((page) => page?.id === selectedPageId);

  const canOpenDropdown = !isLoading && (pages.length > 0 || !!onOpenAddPage);

  const filteredPages = useMemo(() => {
    if (!searchQuery) return pages;
    const query = searchQuery.toLowerCase();
    return pages.filter(
      (page) =>
        page?.name?.toLowerCase().includes(query) ||
        page?.slug?.toLowerCase().includes(query),
    );
  }, [pages, searchQuery]);

  const visiblePages = useMemo(() => {
    return filteredPages.slice(visibleRange.start, visibleRange.end);
  }, [filteredPages, visibleRange]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const scrollPercentage = scrollTop / (scrollHeight - clientHeight);

    if (scrollPercentage > 0.7 && visibleRange.end < filteredPages.length) {
      setVisibleRange((prev) => ({
        start: prev.start,
        end: Math.min(prev.end + 10, filteredPages.length),
      }));
    }
  };

  const handlePageSelect = (pageId: string) => {
    onPageChange?.(pageId);
    setIsOpen(false);
    setSearchQuery("");
    setVisibleRange({ start: 0, end: 10 });
  };

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchQuery("");
        setVisibleRange({ start: 0, end: 10 });
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div
      ref={dropdownRef}
      className={cn(
        "relative shrink-0 transition-all duration-300 min-w-0",
        fluid ? "w-full" : isCompact ? "w-[180px]" : "w-[260px]",
      )}
    >
      <button
        type="button"
        onClick={() => canOpenDropdown && setIsOpen(!isOpen)}
        disabled={!canOpenDropdown}
        className={cn(
          "w-full bg-muted dark:bg-auth-background rounded-full border border-border dark:border-auth-background focus:ring-2 focus:ring-primary h-10 text-sm px-4 flex items-center justify-between gap-2 transition-colors min-w-0",
          !canOpenDropdown && "opacity-50 cursor-not-allowed",
        )}
      >
        <span className="min-w-0 flex-1 truncate text-left">
          {isLoading
            ? "Loading..."
            : pages.length === 0 && !onOpenAddPage
              ? "No pages"
              : pages.length === 0 && onOpenAddPage
                ? "Add page"
                : selectedPage
                  ? selectedPage.name
                  : isCompact
                    ? "Page"
                    : "Select a page"}
        </span>
        <ChevronDown
          className={cn(
            "w-4 h-4 transition-transform ml-2 flex-shrink-0",
            isOpen && "rotate-180",
          )}
        />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-card dark:bg-auth-card border border-border dark:border-auth-background rounded-lg shadow-theme-lg z-[9999] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
          {onOpenAddPage && (
            <button
              type="button"
              onClick={() => {
                onOpenAddPage();
                setIsOpen(false);
                setSearchQuery("");
                setVisibleRange({ start: 0, end: 10 });
              }}
              className="w-full px-4 py-3 text-left text-sm font-medium text-primary bg-card dark:bg-auth-card hover:bg-muted/80 dark:hover:bg-auth-background flex items-center gap-2 border-b border-border dark:border-auth-background"
            >
              <PlusCircle className="w-4 h-4 shrink-0" />
              <span>Add or import page</span>
            </button>
          )}
          {/* Search Bar */}
          <div className="p-2 border-b border-border dark:border-auth-background">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setVisibleRange({ start: 0, end: 10 });
                }}
                placeholder="Search pages..."
                className="w-full pl-9 pr-3 py-2 text-sm bg-background dark:bg-auth-background border border-border dark:border-auth-background rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          {/* Pages List */}
          <div
            ref={listRef}
            onScroll={handleScroll}
            className="max-h-[300px] overflow-y-auto"
          >
            {filteredPages.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                No pages found
              </div>
            ) : (
              <div>
                {visiblePages.map((page) => (
                  <button
                    key={page.id}
                    type="button"
                    onClick={() => handlePageSelect(page.id)}
                    className={cn(
                      "w-full px-4 py-2.5 text-left text-sm hover:bg-muted dark:hover:bg-auth-background transition-colors flex items-center justify-between group",
                      page.id === selectedPageId &&
                        "bg-primary/10 dark:bg-primary/20",
                    )}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{page.name}</div>
                      {page.slug && (
                        <div className="text-xs text-muted-foreground truncate">
                          /{page.slug}
                        </div>
                      )}
                    </div>
                    {page.id === selectedPageId && (
                      <Check className="w-4 h-4 text-primary flex-shrink-0 ml-2" />
                    )}
                  </button>
                ))}
                {visibleRange.end < filteredPages.length && (
                  <div className="p-2 text-center text-xs text-muted-foreground">
                    Scroll for more pages...
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer Info */}
          {filteredPages.length > 0 && (
            <div className="px-4 py-2 border-t border-border dark:border-auth-background text-xs text-muted-foreground">
              {searchQuery
                ? `${filteredPages.length} of ${pages.length} pages`
                : `${pages.length} total pages`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
